import React, { useCallback, useEffect, useMemo, useState} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as PR from "../../../prime-react/index";
import "./MyAnalytics.scss";
import Layout from '../../../layouts/Layout';

import { areDateRangesEqual, defaultDateRange, defaultPlayerDateRange, memberAnalystChartTitles } from '../../../utils/reuse';
import { useSelector } from 'react-redux';
import { useToast } from '../../../context/ToastContext';
import MyAnalyticsGraph from './MyAnalyticsGraph';

const MyAnalyticsDetails = () => {
    const { state } = useLocation();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const isExport = useSelector(state => state.auth?.exportPermission);
    const { graphType, graphColor, secondGraphColor, optionsData, selectedDates, placeholder, responseKey, graphCountKey, secondGraphCountKey, graphNameKey, selectedValuesData, singleSelectedData, selectedLocationsData, selectedCustomerLocationsData, selectedCustomerStatesData, locations } = state ?? {};
    const [selectedValues, setSelectedValues] = useState(selectedValuesData);
    const [singleSelectedValue, setSingleSelectedValue] = useState(singleSelectedData);
    const [dates, setDates] = useState(selectedDates);
    const [visible, setVisible] = useState(false);
    const [apiResponseData, setApiResponseData] = useState([]);
    
    const [selectedLocations, setSelectedLocations] = useState(selectedLocationsData || []);
    const [selectedCustomerLocations, setSelectedCustomerLocations] = useState(selectedCustomerLocationsData || []);
    const [selectedCustomerStates, setSelectedCustomerStates] = useState(selectedCustomerStatesData || []);

    const customerStatesOptions = useMemo(() => {
        if (!selectedCustomerLocations || selectedCustomerLocations.length === 0 || !locations) {
            return [];
        }
        const locationIds = selectedCustomerLocations.map(loc => loc.id);
        const states = locations
            .filter(loc => locationIds.includes(loc.id) && loc.states)
            .flatMap(loc => loc.states)
            .map(stateName => ({ id: stateName, name: stateName }));
        const uniqueStates = [];
        const seen = new Set();
        for (const s of states) {
            if (!seen.has(s.id)) {
                seen.add(s.id);
                uniqueStates.push(s);
            }
        }
        return uniqueStates.sort((a, b) => a.name.localeCompare(b.name));
    }, [selectedCustomerLocations, locations]);

    useEffect(() => {
        if (selectedCustomerStates?.length > 0) {
            const validStateIds = new Set(customerStatesOptions.map(s => s.id));
            const updatedStates = selectedCustomerStates.filter(s => validStateIds.has(s.id));
            if (updatedStates.length !== selectedCustomerStates.length) {
                setSelectedCustomerStates(updatedStates);
            }
        }
    }, [customerStatesOptions, selectedCustomerStates]);
    
    const setExportModalHandler = (value) => {
        setVisible(value);
    }
    useEffect(() => {
        if (!graphType) {
            navigate('/my-analytics');
            return;
        }
    }, [graphType, navigate]);
    const getResponseHandler = useCallback((data) => {
        setApiResponseData(data)
    }, []);
    const filteredValuesForGraph = useMemo(() => {
        if (graphType === 'rtps') {
            return singleSelectedValue ? [singleSelectedValue] : [];
        }
        return selectedValues;
    }, [graphType, singleSelectedValue, selectedValues]);
    const isDefaultDateRange = (graphType === 'pcps' || graphType === 'mcps') ? areDateRangesEqual(dates, defaultPlayerDateRange) : areDateRangesEqual(dates, defaultDateRange);
    const handleClearDates = useCallback(() => {
        setDates((graphType === 'pcps' || graphType === 'mcps') ? defaultPlayerDateRange : defaultDateRange);
    }, [graphType]);  
    return (
        <>
            <Layout>
                <div className='menu-section'>
                    <Link to="/my-analytics" className='back-link'><i className="pi pi-arrow-left"></i> Back to Analytics</Link>
                </div>
                <section className="my-analytics-tab"> 
                    <div className="flex align-items-center justify-content-between gap-3">
                        <div className='heading-card'>
                            <h1>{memberAnalystChartTitles[graphType]?.title}</h1>
                            <p>{memberAnalystChartTitles[graphType]?.description}</p>
                        </div>
                        <div className="header-right flex gap-2 flex-wrap">
                            <div className='date custom-clear-calendar'>
                                <PR.Calendar
                                    value={dates}
                                    onChange={(e) => setDates(e.value)}
                                    selectionMode="range"
                                    iconPos="left"
                                    readOnlyInput
                                    showIcon
                                    className='date-calendar'
                                    placeholder='Date'
                                    dateFormat="dd/mm/yy"
                                    maxDate={new Date()}
                                />
                                {!isDefaultDateRange &&
                                    <div className='custom-clear'>
                                        <i className="pi pi-times text-sm" onClick={handleClearDates}></i>
                                    </div>
                                }
                            </div>
        
                            {placeholder &&
                                (graphType === 'rtps' ?
                                    <div className="flex flex-row gap-3">
                                        <PR.Dropdown
                                            value={singleSelectedValue}
                                            onChange={(e) => setSingleSelectedValue(e.value)}
                                            options={optionsData}
                                            optionLabel="name"
                                            placeholder="Select sports"
                                            className="single-select-dropdown bg-white"
                                            showClear
                                            filter
                                            resetFilterOnHide
                                        /> </div> :
                                    <div className="flex flex-row gap-3">
                                        <PR.MultiSelect
                                            filter
                                            value={selectedValues}
                                            showSelectAll={optionsData?.length <= 15}
                                            onChange={(e) => {
                                                const newValue = e.value || [];

                                                if (newValue.length > 15) {
                                                    showToast('warn', 'You can select a maximum of 15 items');
                                                    return;
                                                }

                                                setSelectedValues(newValue);
                                            }}
                                            options={optionsData}
                                            optionLabel="name"
                                            placeholder={placeholder}
                                            maxSelectedLabels={0}
                                            className="multi-select-dropdown"
                                            resetFilterOnHide
                                            showClear
                                            pt={{
                                                checkboxContainer: {
                                                    onClick: (e) => {
                                                        e.stopPropagation();
                                                        const parent = e.target?.parentNode;
                                                        if (parent && typeof parent.click === 'function') {
                                                            parent.click();
                                                        }
                                                    },
                                                },
                                            }}
                                        />
                                    </div>)


                             }
                            {graphType === 'mcps' && (
                                <>
                                    <div className="flex flex-row gap-3">
                                        <PR.MultiSelect
                                            filter
                                            value={selectedLocations}
                                            showSelectAll={locations?.length <= 15}
                                            onChange={(e) => {
                                                const newValue = e.value || [];
                                                if (newValue.length > 15) {
                                                    showToast('warn', 'You can select a maximum of 15 locations');
                                                    return;
                                                }
                                                setSelectedLocations(newValue);
                                            }}
                                            options={locations}
                                            optionLabel="name"
                                            placeholder="Select Event Locations"
                                            maxSelectedLabels={0}
                                            className="multi-select-dropdown"
                                            resetFilterOnHide
                                            showClear
                                            pt={{
                                                checkboxContainer: {
                                                    onClick: (e) => {
                                                        e.stopPropagation();
                                                        const parent = e.target?.parentNode;
                                                        if (parent && typeof parent.click === 'function') {
                                                            parent.click();
                                                        }
                                                    },
                                                },
                                            }}
                                        />
                                    </div>
                                    <div className="flex flex-row gap-3">
                                        <PR.MultiSelect
                                            filter
                                            value={selectedCustomerLocations}
                                            showSelectAll={locations?.length <= 15}
                                            onChange={(e) => {
                                                const newValue = e.value || [];
                                                if (newValue.length > 15) {
                                                    showToast('warn', 'You can select a maximum of 15 customer locations');
                                                    return;
                                                }
                                                setSelectedCustomerLocations(newValue);
                                            }}
                                            options={locations}
                                            optionLabel="name"
                                            placeholder="Select Customer Locations"
                                            maxSelectedLabels={0}
                                            className="multi-select-dropdown"
                                            resetFilterOnHide
                                            showClear
                                            pt={{
                                                checkboxContainer: {
                                                    onClick: (e) => {
                                                        e.stopPropagation();
                                                        const parent = e.target?.parentNode;
                                                        if (parent && typeof parent.click === 'function') {
                                                            parent.click();
                                                        }
                                                    },
                                                },
                                            }}
                                        />
                                    </div>
                                    <div className="flex flex-row gap-3">
                                        <PR.MultiSelect
                                            filter
                                            value={selectedCustomerStates}
                                            showSelectAll={customerStatesOptions?.length <= 15}
                                            onChange={(e) => {
                                                const newValue = e.value || [];
                                                if (newValue.length > 15) {
                                                    showToast('warn', 'You can select a maximum of 15 customer states');
                                                    return;
                                                }
                                                setSelectedCustomerStates(newValue);
                                            }}
                                            options={customerStatesOptions}
                                            optionLabel="name"
                                            placeholder="Select Customer States"
                                            maxSelectedLabels={0}
                                            disabled={!customerStatesOptions.length}
                                            className="multi-select-dropdown customer-state-dropdown"
                                            resetFilterOnHide
                                            showClear
                                            pt={{
                                                checkboxContainer: {
                                                    onClick: (e) => {
                                                        e.stopPropagation();
                                                        const parent = e.target?.parentNode;
                                                        if (parent && typeof parent.click === 'function') {
                                                            parent.click();
                                                        }
                                                    },
                                                },
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                            <div className="export-block">
                                <PR.Button label="Export" disabled={!isExport || !apiResponseData?.length} onClick={() => { setVisible(true)}}   icon="pi pi-upload" className="export-button" />
                            </div>
                        </div>
                    </div>
                    {graphType &&
                        <MyAnalyticsGraph graphType={graphType} graphColor={graphColor} secondGraphColor={secondGraphColor} responseKey={responseKey} graphCountKey={graphCountKey} secondGraphCountKey={secondGraphCountKey} graphNameKey={graphNameKey} optionsData={optionsData} placeholder={placeholder} dates={dates} isDetailScreen={true} exportVisible={visible} filteredValues={filteredValuesForGraph} setExportModalHandler={setExportModalHandler} getResponseHandler={getResponseHandler} locations={locations} selectedLocationsData={selectedLocations} selectedCustomerLocationsData={selectedCustomerLocations} selectedCustomerStatesData={selectedCustomerStates}></MyAnalyticsGraph>
                    }
                </section>
            </Layout>
        
        </>
    );
};

export default MyAnalyticsDetails;